"use client"

import { mainLogger as log } from "@/app/logger"
import { createUserStore } from "@/app/stores/user"
import { UserStore } from "@/app/stores/user/types"
import { useUser as useAuth0User } from "@auth0/nextjs-auth0"
import { useGrowthBook } from "@growthbook/growthbook-react"
import { useRollbar } from "@rollbar/react"
import { createContext, ReactNode, useContext, useEffect, useRef } from "react"
import { useStore } from "zustand"
import { eventTracker } from "../types"

export type UserStoreApi = ReturnType<typeof createUserStore>

export const UserStoreContext = createContext<UserStoreApi | undefined>(
  undefined
)

type UserStoreProviderProps = {
  children: ReactNode
}

export function UserProvider({ children }: UserStoreProviderProps) {
  const storeRef = useRef<UserStoreApi>()
  if (!storeRef.current) {
    storeRef.current = createUserStore()
  }

  const growthbook = useGrowthBook()
  const rollbar = useRollbar()

  // note "user" is pre-defined property name returned by useAuth0User()
  const { user, error, isLoading } = useAuth0User()

  if (error) log.error("Got error while loading Auth0 user", error)
  if (isLoading) log.info("Still loading Auth0 user")

  useEffect(() => {
    if (storeRef.current && user) {
      storeRef.current.getInitialState().fetchAndUpdateUserData(user)
      eventTracker.trackEvent({ event: "app:new_session" })
    }
  }, [user])

  useEffect(() => {
    if (!user || isLoading || !growthbook.ready) return

    log.info("Growthbook is ready", growthbook.ready)

    growthbook.setAttributes({ email: user.email })
    log.info("Set attributes for growthbook:", growthbook.getAttributes())

    rollbar.configure({
      payload: {
        person: {
          id: user.sub ?? "",
          username: user.name ?? "",
          email: user.email ?? "",
        },
      },
    })
  }, [user, isLoading, growthbook.ready])

  return (
    <UserStoreContext.Provider value={storeRef.current}>
      {children}
    </UserStoreContext.Provider>
  )
}

export const useUserStore = <T,>(selector: (store: UserStore) => T): T => {
  const userStoreContext = useContext(UserStoreContext)

  if (!userStoreContext) {
    throw new Error(`useUserStore must be used within UserStoreProvider`)
  }

  return useStore(userStoreContext, selector)
}
